import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useStore } from '../store/useStore';
import { Avatar } from './UI';
import {
  ChevronLeft, MoreHorizontal, Paperclip, PenLine, ArrowUp, X, FileIcon,
  MessageSquare, Check, CheckCheck, Tag as TagIcon, Download, CircleX, History, Copy, Pencil, Trash2, CheckSquare, XCircle, Maximize2, Minimize2,
} from 'lucide-react';
import { format, isSameDay } from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import { UserProfile } from './UserProfile';
import { ActionModal } from './ActionModal';
import { TagModal } from './TagModal';
import { motion, AnimatePresence } from 'motion/react';
import { BackendTicketStatus, Message as ChatMessage } from '../types';

// ── Status / Priority badge colors ────────────────────────────────────
const statusConfig = {
  open:    { label: 'Открыт',   bg: 'bg-[#34C759]/12', text: 'text-[#1A8934]', dot: 'bg-[#34C759]' },
  pending: { label: 'В работе', bg: 'bg-[#FF9500]/12', text: 'text-[#C47900]', dot: 'bg-[#FF9500]' },
  closed:  { label: 'Закрыт',  bg: 'bg-[#8E8E93]/12', text: 'text-[#6C6C70]', dot: 'bg-[#8E8E93]' },
};
const priorityConfig = {
  high:   { label: 'Высокий', bg: 'bg-[#FF3B30]/12', text: 'text-[#C93028]' },
  medium: { label: 'Средний', bg: 'bg-[#FF9500]/12', text: 'text-[#C47900]' },
  low:    { label: 'Низкий',  bg: 'bg-[#8E8E93]/10', text: 'text-[#6C6C70]' },
};

// ── Info chip ──────────────────────────────────────────────────────────
const InfoChip = ({
  onClick, children, className = '',
}: { onClick?: () => void; children: React.ReactNode; className?: string }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold whitespace-nowrap transition-all active:scale-95 ${className}`}
  >
    {children}
  </button>
);

// ── Templates panel ────────────────────────────────────────────────────
const TemplatesPanel = ({
  templates, operatorName, onSelect, onClose,
}: {
  templates: { id: string; title: string; body: string }[];
  operatorName: string;
  onSelect: (text: string) => void;
  onClose: () => void;
}) => (
  <div className="absolute bottom-[calc(100%+8px)] left-0 right-0 bg-white border border-black/8 shadow-[0_-12px_40px_rgba(0,0,0,0.1)] max-h-[min(320px,50vh)] overflow-y-auto z-20 rounded-2xl">
    <div className="px-4 py-3 border-b border-black/5 flex justify-between items-center sticky top-0 bg-white z-10">
      <span className="font-semibold text-[15px] text-black">Шаблоны ответов</span>
      <button onClick={onClose} className="p-1 hover:bg-black/5 rounded-full transition-colors">
        <X size={18} className="text-[#8E8E93]" />
      </button>
    </div>
    <div className="p-2">
      {templates.map(t => (
        <button
          key={t.id}
          className="w-full text-left p-3 rounded-[12px] hover:bg-[#F2F2F7] transition-colors mb-0.5 group"
          onClick={() => {
            onSelect(t.body.replace('{operator.name}', operatorName.split(' ')[0] || ''));
            onClose();
          }}
        >
          <div className="font-semibold text-[14px] text-black mb-0.5">{t.title}</div>
          <div className="text-[13px] text-[#8E8E93] truncate">{t.body}</div>
        </button>
      ))}
    </div>
  </div>
);

// ── Toast ──────────────────────────────────────────────────────────────
const Toast = ({ message }: { message: string }) => (
  <AnimatePresence>
    {message && (
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2 }}
        className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-black/80 text-white text-[14px] font-medium px-4 py-2 rounded-full backdrop-blur-xl shadow-lg pointer-events-none whitespace-nowrap"
      >
        {message}
      </motion.div>
    )}
  </AnimatePresence>
);

type ConfirmDialogState = {
  title: string;
  message: string;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  onConfirm: () => void | Promise<void>;
};

const ConfirmDialog = ({
  state,
  onClose,
  onConfirm,
}: {
  state: ConfirmDialogState | null;
  onClose: () => void;
  onConfirm: () => void;
}) => (
  <AnimatePresence>
    {state && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[130] bg-black/35 backdrop-blur-[2px]"
          onClick={onClose}
        />
        <motion.div
          initial={{ opacity: 0, y: 14, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ duration: 0.16 }}
          className="fixed z-[131] inset-0 flex items-center justify-center p-4"
        >
          <div className="w-full max-w-[420px] rounded-[18px] bg-white border border-black/10 shadow-[0_24px_80px_rgba(0,0,0,0.22)] p-5">
            <h3 className="text-[18px] font-semibold text-black">{state.title}</h3>
            <p className="mt-2 text-[14px] text-[#6B6B72] leading-[20px]">{state.message}</p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-10 px-4 rounded-[10px] text-[13px] font-medium text-[#3C3C43] bg-[#F2F2F7] hover:bg-[#E8E8EE] transition-colors"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className={`h-10 px-4 rounded-[10px] text-[13px] font-semibold text-white transition-colors ${
                  state.tone === 'danger' ? 'bg-[#FF3B30] hover:bg-[#E2352B]' : 'bg-[#007AFF] hover:bg-[#006CE0]'
                }`}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

const EditMessageDialog = ({
  value,
  onChange,
  onClose,
  onSave,
}: {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
}) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[130] bg-black/35 backdrop-blur-[2px]"
      onClick={onClose}
    />
    <motion.div
      initial={{ opacity: 0, y: 14, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 12, scale: 0.98 }}
      transition={{ duration: 0.16 }}
      className="fixed z-[131] inset-0 flex items-center justify-center p-4"
    >
      <div
        className="w-full max-w-[520px] rounded-[18px] bg-white border border-black/10 shadow-[0_24px_80px_rgba(0,0,0,0.22)] p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-[18px] font-semibold text-black">Редактирование сообщения</h3>
        <p className="mt-2 text-[14px] text-[#6B6B72]">Изменения увидит клиент.</p>
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="mt-3 w-full min-h-[120px] max-h-[240px] resize-y rounded-[12px] border border-black/10 px-3 py-2.5 text-[14px] leading-[20px] text-black outline-none focus:border-[#007AFF]/40 focus:ring-2 focus:ring-[#007AFF]/15"
          placeholder="Введите текст сообщения"
        />
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-[10px] text-[13px] font-medium text-[#3C3C43] bg-[#F2F2F7] hover:bg-[#E8E8EE] transition-colors"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={onSave}
            className="h-10 px-4 rounded-[10px] text-[13px] font-semibold text-white bg-[#007AFF] hover:bg-[#006CE0] transition-colors"
          >
            Сохранить
          </button>
        </div>
      </div>
    </motion.div>
  </AnimatePresence>
);

// ── ChatView ───────────────────────────────────────────────────────────
export const ChatView = () => {
  const {
    activeTicketId, setActiveTicketId, tickets, users, operators, currentOperatorId,
    updateTicketPriority, removeTicketTag,
    sendMessageToApi, updateTicketStatusApi, assignTicketApi, deleteTicket, editMessage, deleteMessage, deleteMessagesBatch,
    templates,
  } = useStore();

  const [inputText, setInputText] = useState('');
  const [isNoteMode, setIsNoteMode] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showPreviousTickets, setShowPreviousTickets] = useState(false);
  const [actionMessage, setActionMessage] = useState<ChatMessage | null>(null);
  const [focusedMessageId, setFocusedMessageId] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<string[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [editDialog, setEditDialog] = useState<{ messageId: string; value: string } | null>(null);
  const [toastMsg, setToastMsg] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageModal, setImageModal] = useState<{ url: string; name?: string } | null>(null);
  const [imageMode, setImageMode] = useState<'fit' | 'original'>('fit');
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastTicketIdRef = useRef<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ticket = tickets.find(t => t.id === activeTicketId);
  const user = users.find(u => u.id === ticket?.userId);
  const previousTickets = useMemo(() => {
    if (!ticket) {
      return [];
    }

    const currentCreatedAt = new Date(ticket.createdAt).getTime();
    const byCustomer = tickets.filter((item) => item.userId === ticket.userId && item.id !== ticket.id);
    const olderFirst = byCustomer
      .filter((item) => new Date(item.createdAt).getTime() < currentCreatedAt)
      .sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime());

    if (olderFirst.length) {
      return olderFirst;
    }

    return byCustomer.sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime());
  }, [ticket, tickets]);

  useEffect(() => {
    if (!ticket) {
      return;
    }
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    const switchedTicket = lastTicketIdRef.current !== ticket.id;
    lastTicketIdRef.current = ticket.id;
    if (switchedTicket) {
      container.scrollTop = container.scrollHeight;
      return;
    }

    const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceToBottom < 120) {
      container.scrollTop = container.scrollHeight;
    }
  }, [ticket?.id, ticket?.messages.length]);

  useEffect(() => {
    setShowPreviousTickets(false);
    setActionMessage(null);
    setFocusedMessageId(null);
    setSelectionMode(false);
    setSelectedMessageIds([]);
    setConfirmDialog(null);
    setEditDialog(null);
    setImageMode('fit');
  }, [ticket?.id]);

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 2200);
  }, []);

  const autoResize = () => {
    const ta = textareaRef.current;
    if (ta) { ta.style.height = 'auto'; ta.style.height = Math.min(ta.scrollHeight, 128) + 'px'; }
  };

  const canEditOrDeleteMessage = useCallback((message: ChatMessage): boolean => {
    return (
      message.senderType === 'operator' &&
      !message.isInternalNote &&
      !message.id.startsWith('local_') &&
      !message.id.startsWith('rating_') &&
      message.status !== 'sending'
    );
  }, []);

  const toggleMessageSelection = useCallback((messageId: string) => {
    setSelectedMessageIds((current) =>
      current.includes(messageId)
        ? current.filter((id) => id !== messageId)
        : [...current, messageId],
    );
  }, []);

  // ── Empty state ──────────────────────────────────────────────────────
  if (!activeTicketId || !ticket || !user) {
    return (
      <div className="flex-1 hidden md:flex items-center justify-center bg-white flex-col gap-3 text-[#8E8E93]">
        <div className="w-20 h-20 bg-[#F5F5F7] rounded-full flex items-center justify-center shadow-inner mb-2">
          <MessageSquare size={30} className="text-[#C7C7CC]" strokeWidth={1.5} />
        </div>
        <p className="text-[16px] font-medium text-[#3C3C43]/60">Выберите тикет</p>
        <p className="text-[13px] text-[#C7C7CC]">для начала переписки</p>
      </div>
    );
  }

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleSend = () => {
    if (!inputText.trim() && !selectedFile) return;
    void sendMessageToApi(ticket.id, {
      text: inputText.trim(),
      isNote: isNoteMode,
      file: selectedFile ?? undefined,
      mediaType: selectedFile ? inferMediaType(selectedFile) : undefined,
    });
    setInputText('');
    setSelectedFile(null);
    setIsNoteMode(false);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleStatusChange = (status: BackendTicketStatus) => {
    updateTicketStatusApi(ticket.id, status);
    const labelMap: Record<BackendTicketStatus, string> = {
      NEW: 'Новый',
      OPEN: 'Открыт',
      IN_PROGRESS: 'В работе',
      WAITING_USER: 'Ждём клиента',
      WAITING_OPERATOR: 'Ждёт оператора',
      CLOSED: 'Закрыт',
      CANCELED: 'Отменён',
      SPAM: 'Спам',
    };
    showToast(`Статус: ${labelMap[status]}`);
  };

  const handlePriorityChange = (priority: 'high' | 'medium' | 'low') => {
    void updateTicketPriority(ticket.id, priority);
    showToast(`Приоритет: ${priorityConfig[priority].label}`);
  };

  const handleAssign = (operatorId: string | null) => {
    assignTicketApi(ticket.id, operatorId);
    const name = operatorId ? operators.find(o => o.id === operatorId)?.name : null;
    showToast(name ? `Назначен: ${name}` : 'Назначение снято');
  };

  const handleDeleteTicket = () => {
    setConfirmDialog({
      title: 'Удалить тикет?',
      message: 'Тикет и вся его история будут удалены без возможности восстановления.',
      confirmLabel: 'Удалить',
      tone: 'danger',
      onConfirm: () => {
        void deleteTicket(ticket.id)
          .then(() => {
            setShowActionModal(false);
            showToast('Тикет удалён');
          })
          .catch((error) => {
            showToast(error instanceof Error ? error.message : 'Не удалось удалить тикет');
          });
      },
    });
  };

  const handleEditMessage = (messageId: string, currentText: string) => {
    setEditDialog({ messageId, value: currentText ?? '' });
  };

  const submitEditMessage = () => {
    if (!editDialog) {
      return;
    }
    const trimmed = editDialog.value.trim();
    if (!trimmed) {
      showToast('Пустое сообщение нельзя сохранить');
      return;
    }
    void editMessage(ticket.id, editDialog.messageId, trimmed)
      .then(() => {
        setActionMessage(null);
        setEditDialog(null);
        showToast('Сообщение обновлено');
      })
      .catch((error) => showToast(error instanceof Error ? error.message : 'Не удалось изменить сообщение'));
  };

  const handleDeleteMessage = (messageId: string) => {
    setConfirmDialog({
      title: 'Удалить сообщение?',
      message: 'Сообщение будет удалено и у клиента, и в панели оператора.',
      confirmLabel: 'Удалить',
      tone: 'danger',
      onConfirm: () => {
        void deleteMessage(ticket.id, messageId)
          .then(() => {
            setActionMessage(null);
            showToast('Сообщение удалено');
          })
          .catch((error) => showToast(error instanceof Error ? error.message : 'Не удалось удалить сообщение'));
      },
    });
  };

  const handleMessageClick = (message: ChatMessage) => {
    if (message.senderType === 'system' || message.isInternalNote) {
      return;
    }
    if (selectionMode) {
      if (!canEditOrDeleteMessage(message)) {
        return;
      }
      toggleMessageSelection(message.id);
      setFocusedMessageId(message.id);
      return;
    }
    setActionMessage(message);
    setFocusedMessageId(message.id);
  };

  const handleCopyMessage = async (message: ChatMessage) => {
    const text = (message.text ?? '').trim();
    if (!text) {
      showToast('В сообщении нет текста для копирования');
      return;
    }
    await navigator.clipboard.writeText(text);
    showToast('Сообщение скопировано');
  };

  const startSelectionMode = (message: ChatMessage) => {
    if (!canEditOrDeleteMessage(message)) {
      showToast('Можно выбрать только исходящие сообщения оператора');
      return;
    }
    setSelectionMode(true);
    setActionMessage(null);
    setSelectedMessageIds([message.id]);
  };

  const cancelSelectionMode = () => {
    setSelectionMode(false);
    setSelectedMessageIds([]);
    setFocusedMessageId(actionMessage?.id ?? null);
  };

  const handleBulkDelete = () => {
    const selectedIds = selectedMessageIds;
    if (!selectedIds.length) {
      showToast('Нет выбранных сообщений');
      return;
    }
    setConfirmDialog({
      title: 'Удалить выбранные сообщения?',
      message: `Будут удалены ${selectedIds.length} сообщений. Действие необратимо.`,
      confirmLabel: 'Удалить',
      tone: 'danger',
      onConfirm: () => {
        void deleteMessagesBatch(ticket.id, selectedIds)
          .then(() => {
            cancelSelectionMode();
            showToast('Сообщения удалены');
          })
          .catch((error) => showToast(error instanceof Error ? error.message : 'Не удалось удалить сообщения'));
      },
    });
  };

  const sConf = statusConfig[ticket.status];
  const pConf = priorityConfig[ticket.priority];
  const currentOperator = operators.find(o => o.id === currentOperatorId);
  const selectedMessages = ticket.messages.filter((message) => selectedMessageIds.includes(message.id));
  const singleSelectedMessage = selectedMessages.length === 1 ? selectedMessages[0] : null;

  return (
    <div className="flex-1 flex h-full bg-white relative overflow-hidden">
      <Toast message={toastMsg} />

      {/* ── Main column ──────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col h-full min-w-0 relative">

        {/* Header */}
        <div className="shrink-0 px-4 h-[60px] border-b border-black/[0.07] flex items-center justify-between bg-white/85 backdrop-blur-2xl sticky top-0 z-10">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setActiveTicketId(null)}
              className="md:hidden text-[#007AFF] p-1 -ml-1.5 hover:opacity-80 transition-opacity shrink-0"
            >
              <ChevronLeft size={28} strokeWidth={2} />
            </button>
            <button
              onClick={() => setShowProfile(p => !p)}
              className="flex items-center gap-2.5 min-w-0 hover:bg-black/5 px-2 py-1 rounded-[10px] transition-all"
            >
              <Avatar name={`${user.firstName} ${user.lastName}`} size="34px" isOnline={user.isOnline} />
              <div className="min-w-0 flex flex-col items-start">
                <span className="text-[15px] font-semibold text-black leading-tight tracking-tight truncate max-w-[160px]">
                  {user.firstName} {user.lastName}
                </span>
                <span className={`text-[11px] font-medium ${user.isOnline ? 'text-[#34C759]' : 'text-[#8E8E93]'}`}>
                  {user.isOnline ? '● В сети' : 'Был(а) недавно'}
                </span>
              </div>
            </button>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => setShowPreviousTickets((value) => !value)}
              className={`h-9 px-2.5 rounded-full flex items-center justify-center gap-1.5 transition-colors ${
                showPreviousTickets
                  ? 'bg-[#007AFF]/12 text-[#007AFF]'
                  : 'text-[#007AFF] hover:bg-[#007AFF]/10'
              }`}
              title="Показать предыдущие тикеты"
            >
              <History size={15} strokeWidth={2.2} />
              <span className="text-[12px] font-semibold">
                Прошлые {previousTickets.length > 0 ? `(${previousTickets.length})` : ''}
              </span>
            </button>
            <button
              onClick={() => setShowActionModal(true)}
              className="w-9 h-9 rounded-full flex items-center justify-center text-[#007AFF] hover:bg-[#007AFF]/10 transition-colors"
              title="Действия"
            >
              <MoreHorizontal size={21} strokeWidth={2} />
            </button>
          </div>
        </div>

        {(actionMessage || selectionMode) && (
          <div className="shrink-0 px-3.5 py-2 border-b border-black/[0.05] bg-white">
            {!selectionMode && actionMessage && (
              <div className="rounded-[14px] border border-[#007AFF]/20 bg-[linear-gradient(180deg,#F7FAFF_0%,#F2F7FF_100%)] px-2.5 py-2">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold text-[#0069D9] uppercase tracking-wider">Выбрано сообщение</p>
                    <p className="text-[12px] text-[#1D1D1F] truncate">
                      {(actionMessage.text || actionMessage.attachment?.name || 'Без текста').trim()}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setActionMessage(null);
                      setFocusedMessageId(null);
                    }}
                    className="h-8 w-8 rounded-[8px] text-[#8E8E93] hover:bg-black/5 inline-flex items-center justify-center shrink-0"
                  >
                    <XCircle size={14} />
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <button
                    onClick={() => void handleCopyMessage(actionMessage)}
                    className="h-8 px-2.5 rounded-[9px] text-[12px] font-medium text-[#007AFF] bg-white hover:bg-[#F2F6FF] border border-[#007AFF]/20 inline-flex items-center gap-1"
                  >
                    <Copy size={13} />
                    Копировать
                  </button>
                  <button
                    onClick={() => handleEditMessage(actionMessage.id, actionMessage.text ?? '')}
                    disabled={!canEditOrDeleteMessage(actionMessage)}
                    className="h-8 px-2.5 rounded-[9px] text-[12px] font-medium text-[#007AFF] bg-white hover:bg-[#F2F6FF] border border-[#007AFF]/20 inline-flex items-center gap-1 disabled:text-[#C7C7CC] disabled:border-black/10 disabled:hover:bg-white"
                  >
                    <Pencil size={13} />
                    Редактировать
                  </button>
                  <button
                    onClick={() => handleDeleteMessage(actionMessage.id)}
                    disabled={!canEditOrDeleteMessage(actionMessage)}
                    className="h-8 px-2.5 rounded-[9px] text-[12px] font-medium text-[#FF3B30] bg-white hover:bg-[#FFF1F0] border border-[#FF3B30]/20 inline-flex items-center gap-1 disabled:text-[#C7C7CC] disabled:border-black/10 disabled:hover:bg-white"
                  >
                    <Trash2 size={13} />
                    Удалить
                  </button>
                  <button
                    onClick={() => startSelectionMode(actionMessage)}
                    disabled={!canEditOrDeleteMessage(actionMessage)}
                    className="h-8 px-2.5 rounded-[9px] text-[12px] font-medium text-[#007AFF] bg-white hover:bg-[#F2F6FF] border border-[#007AFF]/20 inline-flex items-center gap-1 disabled:text-[#C7C7CC] disabled:border-black/10 disabled:hover:bg-white"
                  >
                    <CheckSquare size={13} />
                    Мультивыбор
                  </button>
                </div>
              </div>
            )}

            {selectionMode && (
              <div className="rounded-[12px] border border-black/10 bg-[#F8F8FB] px-2 py-1.5 flex items-center justify-between gap-2">
                <div className="text-[12px] font-semibold text-black/75 px-1">
                  Выбрано: {selectedMessageIds.length}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      if (!singleSelectedMessage) {
                        showToast('Для копирования выберите одно сообщение');
                        return;
                      }
                      void handleCopyMessage(singleSelectedMessage);
                    }}
                    className="h-8 px-2 rounded-[8px] text-[12px] text-[#007AFF] hover:bg-[#007AFF]/10 inline-flex items-center gap-1"
                  >
                    <Copy size={13} />
                    Копировать
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="h-8 px-2 rounded-[8px] text-[12px] text-[#FF3B30] hover:bg-[#FF3B30]/10 inline-flex items-center gap-1"
                  >
                    <Trash2 size={13} />
                    Удалить
                  </button>
                  <button
                    onClick={cancelSelectionMode}
                    className="h-8 px-2 rounded-[8px] text-[12px] text-[#8E8E93] hover:bg-black/5 inline-flex items-center gap-1"
                  >
                    <XCircle size={13} />
                    Отмена
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {showPreviousTickets && (
          <div className="shrink-0 px-4 py-2.5 border-b border-black/[0.06] bg-white">
            {previousTickets.length === 0 ? (
              <p className="text-[12px] text-[#8E8E93]">Для этого клиента нет предыдущих тикетов.</p>
            ) : (
              <div className="max-h-[180px] overflow-y-auto hide-scrollbar flex flex-col gap-1.5">
                {previousTickets.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setShowPreviousTickets(false);
                      setActiveTicketId(item.id);
                    }}
                    className="w-full text-left rounded-[12px] border border-black/[0.07] px-3 py-2 hover:bg-[#F8F8FB] transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-semibold text-black truncate">
                        {item.shortId}
                      </span>
                      <span className={`text-[11px] font-medium ${statusConfig[item.status].text}`}>
                        {statusConfig[item.status].label}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] text-[#8E8E93] flex items-center justify-between gap-2">
                      <span className="truncate">{item.subject}</span>
                      <span>{format(new Date(item.createdAt), 'dd.MM.yyyy HH:mm')}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Info bar ─────────────────────────────────────────────────── */}
        <div className="shrink-0 px-3.5 py-2.5 border-b border-black/[0.05] bg-[#FAFAFA] flex items-center gap-2 overflow-x-auto hide-scrollbar">
          {/* Status chip */}
          <InfoChip
            onClick={() => setShowActionModal(true)}
            className={`${sConf.bg} ${sConf.text}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${sConf.dot}`} />
            {sConf.label}
          </InfoChip>

          {/* Priority chip */}
          <InfoChip
            onClick={() => setShowActionModal(true)}
            className={`${pConf.bg} ${pConf.text}`}
          >
            {pConf.label}
          </InfoChip>

          {/* Divider */}
          {ticket.tags.length > 0 && (
            <div className="w-px h-4 bg-black/10 shrink-0" />
          )}

          {/* Tag chips */}
          {ticket.tags.map(tag => (
            <div
              key={tag}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F2F2F7] border border-black/[0.06] shrink-0"
            >
              <span className="text-[12px] font-medium text-[#3C3C43]">{tag}</span>
                <button
                onClick={() => { void removeTicketTag(ticket.id, tag); }}
                className="w-3.5 h-3.5 rounded-full flex items-center justify-center hover:bg-[#FF3B30]/15 transition-colors group"
              >
                <X size={9} className="text-[#8E8E93] group-hover:text-[#FF3B30] transition-colors" strokeWidth={2.5} />
              </button>
            </div>
          ))}

          {/* Add tag button */}
          <button
            onClick={() => setShowTagModal(true)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-dashed border-[#007AFF]/40 text-[#007AFF] text-[12px] font-medium hover:bg-[#007AFF]/6 transition-colors shrink-0 active:scale-95"
          >
            <TagIcon size={11} strokeWidth={2.5} />
            Тег
          </button>
        </div>

        {/* ── Messages ─────────────────────────────────────────────────── */}
        <div
          ref={messagesContainerRef}
          onClick={() => {
            if (!selectionMode) {
              setActionMessage(null);
              setFocusedMessageId(null);
            }
          }}
          className="flex-1 overflow-y-auto px-4 py-5 hide-scrollbar"
          style={{ background: '#FAFAFA', scrollbarGutter: 'stable both-edges' }}
        >
          {ticket.messages.map((msg, index) => {
            const prevMsg = ticket.messages[index - 1];
            const showDate = !prevMsg || !isSameDay(new Date(msg.timestamp), new Date(prevMsg.timestamp));
            const showTicketMarker = !prevMsg || prevMsg.ticketId !== msg.ticketId;
            const isUser = msg.senderType === 'user';
            const isSystem = msg.senderType === 'system';
            const isGrouped = prevMsg
              && prevMsg.senderType === msg.senderType
              && prevMsg.isInternalNote === msg.isInternalNote
              && new Date(msg.timestamp).getTime() - new Date(prevMsg.timestamp).getTime() < 4 * 60000
              && !showDate;

            return (
              <React.Fragment key={msg.id}>
                {/* Date separator */}
                {showDate && (
                  <div className="flex items-center gap-3 my-5">
                    <div className="flex-1 h-px bg-black/8" />
                    <span className="text-[11px] font-medium text-[#8E8E93] bg-[#FAFAFA] px-2">
                      {format(new Date(msg.timestamp), 'd MMMM', { locale: ru })}
                    </span>
                    <div className="flex-1 h-px bg-black/8" />
                  </div>
                )}

                {showTicketMarker && msg.ticketShortId && (
                  <div className="sticky top-2 z-[1] flex justify-center my-2">
                    <span className="text-[11px] text-[#3C3C43] bg-white/95 border border-black/10 px-2.5 py-1 rounded-full shadow-sm backdrop-blur">
                      Тикет {msg.ticketShortId}
                    </span>
                  </div>
                )}

                {/* System event */}
                {isSystem ? (
                  <div className="flex justify-center my-2.5">
                    <span className="text-[12px] text-[#8E8E93] bg-black/[0.04] px-3 py-1 rounded-full">
                      {msg.text}
                    </span>
                  </div>
                ) : msg.isInternalNote ? (
                  /* Internal note */
                  <div className={`flex justify-center ${isGrouped ? 'mb-1' : 'mb-3'}`}>
                    <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-[16px] px-4 py-3 max-w-[88%] shadow-sm">
                      <div className="flex items-center gap-1.5 text-[#92400E] text-[10px] font-bold mb-1.5 uppercase tracking-widest">
                        <PenLine size={11} strokeWidth={2.5} />
                        Внутренняя заметка
                      </div>
                      <p className="text-[#78350F] text-[14px] leading-[20px] whitespace-pre-wrap">{msg.text}</p>
                      <p className="text-[10px] text-[#A37834] mt-1.5 text-right">
                        {format(new Date(msg.timestamp), 'HH:mm')}
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Regular message */
                  <div className={`flex ${isUser ? 'justify-start' : 'justify-end'} ${isGrouped ? 'mb-0.5' : 'mb-2.5'}`}>
                    <div
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMessageClick(msg);
                      }}
                      className={`max-w-[78%] md:max-w-[70%] px-4 py-2.5 cursor-pointer relative transition-all duration-150 ${
                      isUser
                        ? 'bg-white text-black border border-black/[0.07] shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                        : 'bg-[#007AFF] text-white shadow-[0_2px_8px_rgba(0,122,255,0.25)]'
                    } ${
                      isUser
                        ? isGrouped ? 'rounded-[18px] rounded-tl-[5px]' : 'rounded-[18px] rounded-tl-[5px]'
                        : isGrouped ? 'rounded-[18px] rounded-tr-[5px]' : 'rounded-[18px] rounded-tr-[5px]'
                    } ${
                      selectedMessageIds.includes(msg.id)
                        ? 'ring-2 ring-[#007AFF] shadow-[0_0_0_4px_rgba(0,122,255,0.18)]'
                        : focusedMessageId === msg.id
                          ? 'ring-2 ring-[#007AFF]/55 shadow-[0_0_0_4px_rgba(0,122,255,0.14)]'
                          : 'hover:ring-1 hover:ring-[#007AFF]/30'
                    }`}
                    >
                      {selectedMessageIds.includes(msg.id) && (
                        <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#007AFF] text-white flex items-center justify-center shadow">
                          <Check size={12} strokeWidth={3} />
                        </span>
                      )}
                      {focusedMessageId === msg.id && !selectedMessageIds.includes(msg.id) && (
                        <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-[#007AFF]/90 shadow" />
                      )}

                      {msg.attachment && (
                        <div className="mb-2">
                          {msg.attachment.type === 'image' && msg.attachment.url ? (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setImageModal({ url: msg.attachment!.url, name: msg.attachment?.name });
                                setImageMode('fit');
                              }}
                              className="block"
                            >
                              <img
                                src={msg.attachment.url}
                                alt="Вложение"
                                className="rounded-[12px] w-auto h-auto max-w-full md:max-w-[200px] shadow-sm"
                              />
                            </button>
                          ) : msg.attachment.url ? (
                            <a
                              href={msg.attachment.url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(event) => event.stopPropagation()}
                              className={`${isUser ? 'bg-black/6' : 'bg-white/15'} p-2.5 rounded-[12px] flex items-center gap-3 hover:opacity-90`}
                            >
                              <div className={`p-2 rounded-[8px] ${isUser ? 'bg-white/80' : 'bg-white/20'}`}>
                                <FileIcon size={18} className={isUser ? 'text-black/60' : 'text-white/80'} />
                              </div>
                              <span className="text-[13px] truncate font-medium">{msg.attachment.name}</span>
                            </a>
                          ) : (
                            <div
                              className={`${isUser ? 'bg-black/6' : 'bg-white/15'} p-2.5 rounded-[12px] flex items-center gap-3 opacity-80`}
                            >
                              <div className={`p-2 rounded-[8px] ${isUser ? 'bg-white/80' : 'bg-white/20'}`}>
                                <FileIcon size={18} className={isUser ? 'text-black/60' : 'text-white/80'} />
                              </div>
                              <span className="text-[13px] truncate font-medium">{msg.attachment.name}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {msg.text.trim().length > 0 && (
                        <p className="text-[15px] leading-[22px] whitespace-pre-wrap break-words">{msg.text}</p>
                      )}
                      {!isUser && (
                        <div className="flex justify-end mt-1 gap-1 items-center">
                          <span className="text-[10px] text-white/65">
                            {msg.status === 'sending' ? 'Загрузка...' : format(new Date(msg.timestamp), 'HH:mm')}
                          </span>
                          <span className="inline-flex w-3 h-3 items-center justify-center">
                            {msg.status === 'read'
                              ? <CheckCheck size={13} className="text-white/80" strokeWidth={2.5} />
                              : msg.status === 'sending'
                                ? <span className="w-3 h-3 border border-white/70 border-t-transparent rounded-full animate-spin" />
                                : <Check size={13} className="text-white/50" strokeWidth={2.5} />
                            }
                          </span>
                        </div>
                      )}
                      {isUser && (
                        <p className="text-[10px] text-[#8E8E93] mt-1 text-right">
                          {format(new Date(msg.timestamp), 'HH:mm')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Input area ───────────────────────────────────────────────── */}
        <div className="shrink-0 px-3 pb-[calc(env(safe-area-inset-bottom)+8px)] md:pb-3 pt-3 bg-white border-t border-black/[0.07] relative">
          {showTemplates && (
            <TemplatesPanel
              templates={templates}
              operatorName={currentOperator?.name || ''}
              onSelect={(text) => {
                setInputText(text);
                setTimeout(() => { textareaRef.current?.focus(); autoResize(); }, 50);
              }}
              onClose={() => setShowTemplates(false)}
            />
          )}

          {/* Note mode banner */}
          {isNoteMode && (
            <div className="mb-2 flex items-center gap-2 px-1">
              <PenLine size={13} className="text-[#D48806]" strokeWidth={2.5} />
              <span className="text-[12px] font-semibold text-[#D48806]">Внутренняя заметка — не видна клиенту</span>
            </div>
          )}

          {selectedFile && (
            <div className="mb-2 flex items-center justify-between gap-2 rounded-[14px] bg-[#F2F2F7] px-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-[13px] font-medium text-black">{selectedFile.name}</div>
                <div className="text-[11px] text-[#8E8E93]">{Math.round(selectedFile.size / 1024)} KB</div>
              </div>
              <button
                onClick={() => setSelectedFile(null)}
                className="text-[#8E8E93] hover:text-[#FF3B30] transition-colors"
              >
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
          )}

          <div className={`flex items-end gap-2 rounded-[20px] px-3 py-2 border transition-all duration-200 ${
            isNoteMode
              ? 'bg-[#FFFBEB] border-[#FDE68A] shadow-[0_0_0_3px_rgba(253,230,138,0.3)]'
              : 'bg-[#F2F2F7] border-transparent focus-within:bg-white focus-within:border-black/10 focus-within:shadow-[0_2px_16px_rgba(0,0,0,0.07)]'
          }`}>
            {/* Left actions */}
            <div className="flex gap-0.5 pb-0.5 shrink-0">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-8 h-8 rounded-full flex items-center justify-center text-[#8E8E93] hover:text-[#007AFF] hover:bg-[#007AFF]/8 transition-colors cursor-pointer"
                title="Прикрепить файл"
              >
                <Paperclip size={18} strokeWidth={2} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  setSelectedFile(file);
                  event.currentTarget.value = '';
                }}
              />
              <button
                onClick={() => setIsNoteMode(v => !v)}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  isNoteMode
                    ? 'text-[#D48806] bg-[#FDE68A]/60'
                    : 'text-[#8E8E93] hover:text-[#007AFF] hover:bg-[#007AFF]/8'
                }`}
                title="Внутренняя заметка"
              >
                <PenLine size={18} strokeWidth={2} />
              </button>
              <button
                onClick={() => setShowTemplates(v => !v)}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  showTemplates ? 'bg-[#007AFF]/12 text-[#007AFF]' : 'text-[#8E8E93] hover:text-[#007AFF] hover:bg-[#007AFF]/8'
                }`}
                title="Шаблоны"
              >
                <MessageSquare size={18} strokeWidth={2} />
              </button>
            </div>

            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={inputText}
              onChange={e => { setInputText(e.target.value); autoResize(); }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
              }}
              placeholder={isNoteMode ? 'Написать заметку...' : 'Написать сообщение...'}
              className={`flex-1 bg-transparent border-none outline-none resize-none py-1 text-[16px] md:text-[15px] leading-[22px] placeholder:text-[#B0B0B8] ${
                isNoteMode ? 'text-[#78350F] placeholder:text-[#D48806]/50' : 'text-black'
              }`}
              rows={1}
              style={{ minHeight: '32px', maxHeight: '128px' }}
            />

            {/* Send button */}
            <div className="pb-0.5 shrink-0">
              <button
                onClick={handleSend}
                disabled={!inputText.trim() && !selectedFile}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                  inputText.trim() || selectedFile
                    ? isNoteMode
                      ? 'bg-[#FFCC00] text-black shadow-[0_2px_8px_rgba(255,204,0,0.4)] active:scale-90'
                      : 'bg-[#007AFF] text-white shadow-[0_2px_8px_rgba(0,122,255,0.4)] active:scale-90'
                    : 'bg-black/8 text-[#8E8E93]'
                }`}
              >
                <ArrowUp size={17} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      <ActionModal
        isOpen={showActionModal}
        onClose={() => setShowActionModal(false)}
        ticket={ticket}
        operators={operators}
        onStatusChange={handleStatusChange}
        onPriorityChange={handlePriorityChange}
        onAssign={handleAssign}
        onDeleteTicket={handleDeleteTicket}
      />

      <TagModal
        isOpen={showTagModal}
        onClose={() => setShowTagModal(false)}
        ticketId={ticket.id}
        tags={ticket.tags}
      />

      {/* Profile panel */}
      {showProfile && (
        <UserProfile userId={user.id} onClose={() => setShowProfile(false)} />
      )}

      {imageModal && (
        <div className="fixed inset-0 z-[120] bg-[radial-gradient(circle_at_top,#182338_0%,#0A0A0F_65%)] backdrop-blur-md flex flex-col">
          <div className="flex justify-between items-center px-4 py-3 border-b border-white/10 bg-black/20">
            <div className="min-w-0">
              <p className="text-white text-[13px] font-medium truncate pr-3">{imageModal.name || 'Изображение'}</p>
              <p className="text-white/55 text-[11px] mt-0.5">
                {imageMode === 'fit' ? 'Режим: Вписать в экран' : 'Режим: Оригинальный размер (со скроллом)'}
              </p>
            </div>
            <button
              onClick={() => setImageModal(null)}
              className="text-white/90 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors shrink-0"
              title="Закрыть"
            >
              <X size={18} strokeWidth={2.4} />
            </button>
          </div>

          <div className="flex-1 overflow-auto p-2 md:p-4">
            <div className={`min-h-full flex ${imageMode === 'fit' ? 'items-center justify-center' : 'items-start justify-start'}`}>
              <img
                src={imageModal.url}
                alt={imageModal.name || 'Вложение'}
                className={`rounded-[12px] ${
                  imageMode === 'fit'
                    ? 'max-w-full max-h-[calc(100vh-170px)] object-contain shadow-[0_20px_60px_rgba(0,0,0,0.45)]'
                    : 'max-w-none h-auto object-none shadow-[0_20px_60px_rgba(0,0,0,0.45)]'
                }`}
              />
            </div>
          </div>

          <div className="p-3 md:p-4 grid grid-cols-3 gap-2 border-t border-white/10 bg-black/20">
            <button
              onClick={() => setImageMode((prev) => (prev === 'fit' ? 'original' : 'fit'))}
              className="text-[12px] md:text-[13px] font-medium py-2.5 rounded-[10px] bg-white/12 text-white hover:bg-white/18 transition-colors inline-flex items-center justify-center gap-2"
            >
              {imageMode === 'fit' ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
              {imageMode === 'fit' ? 'Оригинал' : 'Вписать'}
            </button>
            <a
              href={imageModal.url}
              download={imageModal.name || 'image'}
              className="text-[12px] md:text-[13px] font-medium py-2.5 rounded-[10px] bg-white/12 text-white hover:bg-white/18 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Download size={14} />
              Скачать
            </a>
            <button
              onClick={() => setImageModal(null)}
              className="text-[12px] md:text-[13px] font-medium py-2.5 rounded-[10px] bg-[#FF3B30]/85 text-white hover:bg-[#FF3B30] transition-colors inline-flex items-center justify-center gap-2"
            >
              <CircleX size={14} />
              Закрыть
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        state={confirmDialog}
        onClose={() => setConfirmDialog(null)}
        onConfirm={() => {
          const pending = confirmDialog;
          setConfirmDialog(null);
          if (pending) {
            void pending.onConfirm();
          }
        }}
      />

      {editDialog && (
        <EditMessageDialog
          value={editDialog.value}
          onChange={(value) => setEditDialog((current) => (current ? { ...current, value } : current))}
          onClose={() => setEditDialog(null)}
          onSave={submitEditMessage}
        />
      )}
    </div>
  );
};

function inferMediaType(file: File): 'PHOTO' | 'DOCUMENT' | 'VIDEO' | 'VOICE' | 'AUDIO' | 'OTHER' {
  if (file.type.startsWith('image/')) return 'PHOTO';
  if (file.type.startsWith('video/')) return 'VIDEO';
  if (file.type.startsWith('audio/')) return 'AUDIO';
  if (file.type) return 'DOCUMENT';
  return 'OTHER';
}
